import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData } from '../../api';
import { getPassOrderPages } from '../../utils/AccountUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import { useLanguage } from '../../common/GeneralUtils';
import { ALL_PASS_PAYMENT_STATUSES, ALL_PASS_STATUSES, ALL_PASS_TYPES, usePassesFilter } from './PassesFilterContext';

type SummaryLabel = { primary: string; secondary: string };

export const usePassesFilterSummary = (): SummaryLabel => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { isFilterActive, statuses, paymentStatuses, passTypes, orderPageIds, locationIds } = usePassesFilter();

  return useMemo(() => {
    if (!isFilterActive) return { primary: t('passes.filter.label'), secondary: '' };

    const parts: string[] = [];

    if (statuses.length > 0 && statuses.length < ALL_PASS_STATUSES.length) {
      parts.push(statuses.map((s) => t(`passes.status.${s}`)).join(', '));
    }

    if (passTypes.length > 0 && passTypes.length < ALL_PASS_TYPES.length) {
      parts.push(passTypes.map((pt) => t(`passes.pass_type.${pt}`)).join(', '));
    }

    if (orderPageIds !== null) {
      const total = getPassOrderPages(account).length;
      parts.push(t('passes.filter.summary.subscriptions', { count: orderPageIds.length, total }));
    }

    if (locationIds !== null) {
      const totalLocations = (account.locations ?? []).length + 1;
      parts.push(t('passes.filter.summary.locations', { count: locationIds.length, total: totalLocations }));
    }

    const secondaryParts: string[] = [];
    if (paymentStatuses.length > 0 && paymentStatuses.length < ALL_PASS_PAYMENT_STATUSES.length) {
      secondaryParts.push(paymentStatuses.map((s) => t(`passes.payment_status.${s}`)).join(', '));
    }

    return {
      primary: parts.join(' · ') || t('passes.filter.label'),
      secondary: secondaryParts.join(' · '),
    };
  }, [isFilterActive, statuses, paymentStatuses, passTypes, orderPageIds, locationIds, account, language, t]);
};
